import { FormLabel, LabeledTextArea, LabeledTextInput } from '../../generic/Forms';
import LicenseEditableList from '../../licenses/EditableList';
import type { RevisionLicense } from '../../../types';

import { ModalAddDependencyAlert } from './ModalAddDependencyAlert';
import type { UserDefinedModalData } from './types';

type AddUserDefinedDependencyProps = {
  onChange: (dep: UserDefinedModalData) => void;
  dependencyToAdd: UserDefinedModalData;
  isDisabled: boolean;
};

type LicenseChange = {
  editing: boolean;
  licenses: Array<Partial<RevisionLicense>>;
};

const AddUserDefinedDependency = ({ isDisabled, onChange, dependencyToAdd }: AddUserDefinedDependencyProps) => {
  const onAttributeChange = (
    attr: 'name' | 'version' | 'homepage' | 'description' | 'licenses',
    value: string | Array<Partial<RevisionLicense>> | undefined,
  ) => {
    onChange({
      ...dependencyToAdd,
      kind: 'user',
      [attr]: value,
    });
  };

  // we only allow 1 license group in this modal, so pick the first one and set
  // TODO: FOSSA should support multiple groups when adding manual dependencies
  const onLicenseChange = (licenseGroups: LicenseChange[]) => {
    onAttributeChange('licenses', licenseGroups[0]?.licenses);
  };

  return (
    <div className="userdefined-panel">
      <p className="userdefined-panel__title">
        Create your dependency from scratch
      </p>
      <ModalAddDependencyAlert dependencyToAdd={dependencyToAdd} />
      <div className="userdefined-panel__body">
        <div className="userdefined-panel__body__section userdefined-panel__body__name-version">
          <LabeledTextInput
            type="text"
            label="Name"
            value={dependencyToAdd.name || ''}
            placeholder="Dependency"
            isRequired
            isDisabled={isDisabled}
            onChange={n => onAttributeChange('name', n)}
          />
          <LabeledTextInput
            type="text"
            label="Version"
            value={dependencyToAdd.version || ''}
            placeholder="1.2.3"
            isRequired
            isDisabled={isDisabled}
            onChange={v => onAttributeChange('version', v)}
          />
        </div>
        <div className="userdefined-panel__body__section userdefined-panel__body__licenses">
          <FormLabel
            label="License"
            requiredText="(Required)"
          >
            <LicenseEditableList
              defaultLicenseGroups={[]}
              onChange={onLicenseChange}
              canSplit={false}
              canEdit={!isDisabled}
              maxLicenseGroups={1}
              hideLabel
              hideFileMatches
              shouldSkipWarning
            />
          </FormLabel>
        </div>
        <div className="userdefined-panel__body__section">
          <LabeledTextInput
            type="text"
            label="Homepage"
            value={dependencyToAdd.homepage || ''}
            placeholder="www.dependency.com"
            isDisabled={isDisabled}
            onChange={hp => onAttributeChange('homepage', hp)}
          />
        </div>
        <div className="userdefined-panel__body__section">
          <LabeledTextArea
            label="Description"
            value={dependencyToAdd.description || ''}
            isDisabled={isDisabled}
            onChange={(d: string) => onAttributeChange('description', d)}
          />
        </div>
      </div>
    </div>
  );
};

export default AddUserDefinedDependency;
